import { Component, OnInit, OnDestroy, ElementRef } from '@angular/core';
import { IonicPage, ModalController, AlertController } from 'ionic-angular';
import { PollData } from '../../providers/poll';
import { Store } from '@ngrx/store';
import * as clientSelectors from '../../ngrx/reducers/client.reducer';
import * as userSelectors from '../../ngrx/reducers/auth.reducer';
import { Subscription } from 'rxjs';
import { PollActiveComponent } from './components/poll-active/poll-active';
import { IPoll } from '../../models/poll.model';
import { IUser } from '../../models/user.model';
import { UtilitiesProvider } from '../../providers/utilities';
import { TranslateCodePipe } from '../../pipes/translate';

@IonicPage({
  segment: 'poll'
})
@Component({
  selector: 'page-poll',
  templateUrl: 'poll.html',
})
export class PollPage implements OnInit, OnDestroy {

  public alertsPolls: ElementRef;
  public clientIdSubs: Subscription;
  public clientId: string;
  public currentUserSubs: Subscription;
  public currentUser: IUser;
  public activePollInformation: IPoll;
  public moduleStateSubs: Subscription;
  public moduleState: boolean;
  private activePollSubs: Subscription;
  private pollInformationSubs: Subscription;
  private canShowActivePollToUserSubs: Subscription;
  private saveClientPollStatisticAndViewSubs: Subscription;

  constructor(
    private pollData: PollData,
    private store: Store<{}>,
    private modalController: ModalController,
    private alertController: AlertController,
    public element: ElementRef,
    private utilities: UtilitiesProvider,
    private translatePipe: TranslateCodePipe
  ) { }

  ngOnInit() {
    this.getClientId();
  }

  /**
   * Get clientId
   * 1.Get the clientId, then execute getCurrentUser() method.
   * @author Sebastián Guevara
  */
  private getClientId() {
    this.clientIdSubs = this.store.select(clientSelectors.getClientId).subscribe((clientId) => {
      if (clientId) {
        this.clientId = clientId;
        this.getCurrentUser();
      }
    })
  }

  /**
   *  Get the currentUser
   * 1. Get the currentUser, then execute getActivePoll() method.
   * @author Sebastián Guevara
   */
  private getCurrentUser() {
    this.currentUserSubs = this.store.select(userSelectors.getUser)
      .subscribe((user: IUser) => {
        if (user) {
          this.currentUser = user;
          this.getModuleState();
        }
      })
  }

  public getModuleState() {
    this.moduleStateSubs = this.store.select(clientSelectors.getClientSingleModule('opinometer')).subscribe((moduleState => {
      if (moduleState) {
        this.moduleState = true;
        this.getActivePoll();
      } else {
        this.moduleState = false;
      }
    }));

  }

  /**
   * Get the active base on the general interactivity
   * 1. If a poll is active, get the information about it.
   * @author Sebastián Guevara
   */
  private getActivePoll() {
    this.activePollSubs = this.pollData.getClientActivePoll(this.clientId)
      .subscribe((pollId) => {
        if (pollId && this.moduleState) {
          this.getPollInformation(<string>pollId);
        }
      });
  }

  /**
   * Get the poll information based on the pollId
   * @param pollId
   * @author Sebastián Guevara
   */
  public getPollInformation(pollId: string) {
    this.pollInformationSubs = this.pollData.getPollByClientId(this.clientId, pollId).subscribe((pollInformation: IPoll) => {
      this.utilities.consoleDecorator('pollInformation', pollInformation);
      this.activePollInformation = pollInformation;
      this.verifyUseAnswerByPoll(pollInformation);
    })
  }

  /**
   * Verify if the user has an answer for the poll
   * 1. If the user no has answers, and the poll is alert, open a alert that says "New poll is Active",
   * if the user click on the open button will execute the method openModal
   * 2. If user has answer, do not do anything.
   * @author Sebastián Guevara
   */
  public verifyUseAnswerByPoll(pollInformation: IPoll) {
    this.canShowActivePollToUserSubs = this.pollData.canShowActivePollToUser(this.clientId, pollInformation.general.id, this.currentUser.info.id).subscribe(async (pollPermissions) => {
      this.utilities.consoleDecorator('pollPermissions', pollPermissions);
      if (pollPermissions.show) {
        if (pollInformation.general.isAlert) {
          this.alertController.create({
            title: await this.translatePipe.transform('A new survey is active'),
            buttons: [
              {
                text: await this.utilities.translateTextOrObject('Open'),
                handler: () => {
                  this.openPollModal();
                  this.saveClientPollStatisticAndViewSubs = this.pollData.saveClientPollStatisticAndView(this.activePollInformation.general.id, this.currentUser.info.id).subscribe(() => { });
                  let elements: NodeListOf<Element> = document.getElementsByClassName("alert-md");
                  if (elements) {
                    for (let index = 0; index < elements.length; index++) {
                      const element = elements[index];
                      element.setAttribute("style", "display:none !important;");
                    }
                  }
                }
              },
              {
                text: 'Cancel',
                handler: () => {
                  this.saveClientPollStatisticAndViewSubs = this.pollData.saveClientPollStatisticAndView(this.activePollInformation.general.id, this.currentUser.info.id).subscribe(() => { });
                  let elements: NodeListOf<Element> = document.getElementsByClassName("alert-md");
                  if (elements) {
                    for (let index = 0; index < elements.length; index++) {
                      const element = elements[index];
                      element.setAttribute("style", "display:none !important;");
                    }
                  }
                }
              }
            ]
          }).present();
        }
      }
    })
  }


  /**
   * Open the pollActive Modal
   * @author Sebastián Guevara
   */
  public openPollModal() {
    this.modalController.create(PollActiveComponent, {
      clientId: this.clientId,
      pollId: this.activePollInformation.general.id
    }, {
        enableBackdropDismiss: false
      }).present();

  }

  ngOnDestroy() {
    if (this.clientIdSubs) this.clientIdSubs.unsubscribe();
    if (this.currentUserSubs) this.currentUserSubs.unsubscribe();
    if (this.moduleStateSubs) this.moduleStateSubs.unsubscribe();
    if (this.activePollSubs) this.activePollSubs.unsubscribe();
    if (this.pollInformationSubs) this.pollInformationSubs.unsubscribe();
    if (this.canShowActivePollToUserSubs) this.canShowActivePollToUserSubs.unsubscribe();
    if (this.saveClientPollStatisticAndViewSubs) this.saveClientPollStatisticAndViewSubs.unsubscribe();
  }

}



// WEBPACK FOOTER //
// ./src/pages/poll/poll.ts